import { reaction } from 'mobx';
import { Instance, applySnapshot, destroy, detach, flow, types } from 'mobx-state-tree';

import {
	getFilterStorageKey,
	getFilterStoragePrefixWithPage,
	getSystemStorageKey,
	getSystemStoragePrefixWithPage,
} from '@common/constants/StoragePrefix';
import Service from '@common/models/Services/Service';
import { storeReference } from '@common/models/helpers';

export interface ITableDataQuery {
	search?: string;
	page?: number;
	limit?: number;
	[key: string]: string | number | number[] | string[] | boolean;
}

const TableData = types
	.model('TableData', {
		data: types.optional(types.array(types.frozen()), []),
		service: types.maybe(storeReference(Service)),
		filters: types.optional(types.frozen(), {}),
		limit: types.optional(types.number, 25),
		page: types.optional(types.number, 1),
		search: types.optional(types.string, ''),
		syncUrl: types.optional(types.boolean, true),
		syncStorage: types.optional(types.boolean, true),
		appendMode: types.optional(types.boolean, false),
	})
	.volatile(() => ({
		loading: true,
		abortController: null as AbortController | null,
		hasMore: true,
		isInitialLoading: true,
	}))
	.views(() => ({}))
	.actions(() => ({
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		_fetch: (_: ITableDataQuery = {}, config?: any) => new Promise(() => {}),
		_remove: (_: number) => new Promise(() => {}),
	}))
	.actions((self) => ({
		setAppendMode(value: boolean) {
			self.appendMode = value;
		},
		setHasMore(value: boolean) {
			self.hasMore = value;
		},
		clearPageRelatedSessionStorage() {
			if (!self.syncStorage) return;

			const keysToRemove: string[] = [];

			const filterPrefix = getFilterStoragePrefixWithPage(window.location.pathname);
			const systemPrefix = getSystemStoragePrefixWithPage(window.location.pathname);

			Object.keys(sessionStorage).forEach((key) => {
				if (key.startsWith(filterPrefix) || key.startsWith(systemPrefix)) {
					sessionStorage.removeItem(key);
				}
			});

			keysToRemove.forEach((key) => sessionStorage.removeItem(key));
		},
		setSearch(search: string) {
			self.search = search;
			self.page = 1;
		},
		setPage(page: number) {
			self.page = page;
		},
		setLimit(limit: number) {
			if (self.syncStorage) {
				const limitKey = getFilterStorageKey(window.location.pathname, 'limit');
				sessionStorage.setItem(limitKey, limit.toString());
			}
			self.limit = limit;
			self.page = 1;
		},
		setFilters(newFilters: Record<string, any>) {
			self.filters = newFilters;
			self.page = 1;
		},
		fetch: flow(function* (query: ITableDataQuery = {}) {
			if (self.abortController) {
				self.abortController.abort();
			}

			self.abortController = new AbortController();
			const { signal } = self.abortController;

			const { search } = self;

			const isFirstPage = (query.page || self.page) === 1;

			const shouldAppend = !isFirstPage && self.appendMode;

			self.loading = true;

			if (self.isInitialLoading) {
				self.isInitialLoading = true;
			}
			try {
				const result = yield self._fetch(
					{
						...(search && { search }),
						page: self.page,
						limit: self.limit,
						...(self.service && { service_id: self.service.id }),
						...self.filters,
						...query,
					},
					{ signal },
				);

				if (search !== self.search) {
					return;
				}

				self.hasMore = Array.isArray(result) && result.length >= self.limit;

				if (shouldAppend) {
					self.data.push(...result);
				} else {
					applySnapshot(self.data, result);
				}
			} catch (error) {
				if (error?.code !== 'ERR_CANCELED') {
					throw error;
				}
			} finally {
				if (!signal.aborted) {
					self.loading = false;
					self.abortController = null;
				}

				if (self.isInitialLoading) {
					self.isInitialLoading = false;
				}
			}
		}),
		removeItem: flow(function* (model: any) {
			detach(model);
			yield self._remove(model.id);
			destroy(model);
		}),
	}))
	.actions((self) => ({
		afterCreate() {
			if (self.syncUrl) {
				reaction(
					() => ({
						page: self.page,
						search: self.search,
						filters: self.filters,
						limit: self.limit,
					}),
					({ search, page, filters, limit }) => {
						const currentParams = new URLSearchParams(window.location.search);

						const additionalParams: Record<string, string> = {};
						currentParams.forEach((value, key) => {
							if (!['page', 'limit', 'search', ...Object.keys(filters)].includes(key)) {
								additionalParams[key] = value;
							}
						});

						const params = new URLSearchParams({
							page: page.toString(),
							...(limit && { limit: limit.toString() }),
							...(search && { search }),
						});

						Object.entries(filters).forEach(([key, value]) => {
							if (value !== null && value !== undefined) {
								params.append(key, value as string);
							}
						});

						Object.entries(additionalParams).forEach(([key, value]) => {
							params.set(key, value);
						});

						const newUrl = `${window.location.pathname}?${params.toString()}`;
						window.history.pushState(null, '', newUrl);
					},
				);
			}

			if (self.syncStorage) {
				reaction(
					() => ({
						filters: self.filters,
						limit: self.limit,
					}),
					({ filters, limit }) => {
						self.clearPageRelatedSessionStorage();

						Object.entries(filters).forEach(([key, value]) => {
							const filterKey = getFilterStorageKey(window.location.pathname, key);
							if (value !== null && value !== undefined) {
								sessionStorage.setItem(filterKey, value as string);
							}
						});

						const limitKey = getSystemStorageKey(window.location.pathname, 'limit');
						sessionStorage.setItem(limitKey, limit.toString());
					},
				);
			}
		},
	}));

export default TableData;

export interface ITableDataModel extends Instance<typeof TableData> {}
